import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import {
  formatPrice,
  getSellerPayoutBreakdown,
  isCardPaymentOrder,
} from '../components/utils/utils';
import {
  orderStatuses,
  paymentStatuses,
  shippingStatuses,
} from '../constants/shipping';
import logoSrc from '../assets/trade-net-rx-logo.png';

export type OrderHistoryExportMode = 'buy' | 'sell';

export type GroupedOrders = Record<string, Record<string, any[]>>;

type ExportOrderHistoryParams = {
  orders: GroupedOrders;
  fromDate: moment.Moment | null;
  toDate: moment.Moment | null;
  mode: OrderHistoryExportMode;
  isAdmin?: boolean;
};

const GROUND_SHIPPING_CUTOFF = new Date('Mon Feb 20 2025 23:59:59 GMT+0400');

const stripOrderPrefix = (orderKey: string): string =>
  orderKey.startsWith('o_') ? orderKey.slice(2) : orderKey;

const getStatusLabel = (order: any): string => {
  if (order?.status === 0 || order?.status === 2) {
    return orderStatuses[order.status]?.label || 'N/A';
  }
  if (
    (order?.shippingStatus === 5 && order?.paymentStatus === 1) ||
    order?.paymentStatus === 2
  ) {
    return paymentStatuses[order?.paymentStatus]?.label || 'N/A';
  }
  return shippingStatuses[order?.shippingStatus]?.label || 'N/A';
};

const getTrackingId = (order: any): string => {
  if (order?.status === 0 || order?.status === 2) return '-';
  return order?.trackingNumber || '-';
};

const getPaymentMethod = (order: any): string =>
  isCardPaymentOrder(order ?? {}) ? 'Card' : 'ACH';

const getActiveOrders = (groupOrders: any[]) =>
  groupOrders.filter((order) => order.status === 1);

const getBuyerPaidShippingFee = (item: any): number => {
  const orderDate = new Date(item.createdAt);
  const isNewOrderForGround = orderDate > GROUND_SHIPPING_CUTOFF;
  const isFedexGround = item.ShippingMethod?.key === 'fedex-ground';
  const isShippingPaidBySeller = Boolean(item.isShippingPaidBySeller);

  // Ground (seller-paid) and any seller-paid shipping are not charged to buyer
  if (isShippingPaidBySeller || (isNewOrderForGround && isFedexGround)) {
    return 0;
  }
  return Number(item.shippingFee) || 0;
};

const getBuyerCreditTotal = (groupOrders: any[]): string => {
  const filteredOrders = getActiveOrders(groupOrders);
  let total = 0;
  filteredOrders.forEach((item) => {
    total += Number(item.totalAmount) + getBuyerPaidShippingFee(item);
  });
  return total.toFixed(2);
};

const getOrderFinancials = (
  groupOrders: any[],
  mode: OrderHistoryExportMode
) => {
  const activeOrders = getActiveOrders(groupOrders);
  const breakdown = getSellerPayoutBreakdown(activeOrders);
  const totalOrderValue = activeOrders.reduce(
    (sum, item) => sum + Number(item.totalAmount || 0),
    0
  );

  // Buy: only shipping the buyer actually pays (2-day / priority).
  // Sell: only shipping deducted from seller (ground / seller-paid); buyer-paid = 0.
  const shippingFee =
    mode === 'buy'
      ? activeOrders.reduce((sum, item) => sum + getBuyerPaidShippingFee(item), 0)
      : breakdown?.shippingFeeDeducted ?? 0;

  const tradeNetFee =
    breakdown?.tradeNetFee ??
    activeOrders.reduce(
      (sum, item) =>
        sum + (Number(item.totalAmount) * Number(item.tradeNetFee || 0)) / 100,
      0
    );
  const fedExInsurance =
    breakdown?.fedExInsurance ??
    Number(activeOrders[0]?.fedExInsurance || 0);
  const transactionFee = breakdown?.transactionFee ?? 0;

  return {
    totalOrderValue: formatPrice(totalOrderValue),
    shippingFee: formatPrice(shippingFee),
    transactionFee: formatPrice(transactionFee),
    tradeNetRxFee: formatPrice(tradeNetFee),
    fedExInsurance: formatPrice(fedExInsurance),
    creditTotal: breakdown ? breakdown.creditTotal.toFixed(2) : '0.00',
  };
};

const buildFilename = (
  mode: OrderHistoryExportMode,
  isAdmin: boolean,
  fromDate: moment.Moment | null,
  toDate: moment.Moment | null,
  extension: 'xlsx' | 'pdf'
): string => {
  const prefix = isAdmin
    ? mode === 'buy'
      ? 'sales'
      : 'orders'
    : mode === 'buy'
      ? 'buy-history'
      : 'sell-history';
  const from = fromDate ? fromDate.format('MM-DD-YYYY') : 'start';
  const to = toDate ? toDate.format('MM-DD-YYYY') : 'end';
  return `${prefix}_${from}_to_${to}.${extension}`;
};

const getExportTitle = (mode: OrderHistoryExportMode, isAdmin: boolean): string => {
  if (isAdmin) return mode === 'buy' ? 'Sales' : 'Orders';
  return mode === 'buy' ? 'Buy History' : 'Sell History';
};

const buildOrderRows = ({
  orders,
  mode,
  isAdmin = false,
}: Omit<ExportOrderHistoryParams, 'fromDate' | 'toDate'>): Record<string, string | number>[] => {
  const rows: Record<string, string | number>[] = [];

  Object.keys(orders).forEach((orderKey) => {
    const orderNumber = stripOrderPrefix(orderKey);
    Object.keys(orders[orderKey] || {})
      .sort()
      .forEach((groupKey) => {
        const groupOrders = orders[orderKey][groupKey] || [];
        const currentOrder = groupOrders[0];
        if (!currentOrder) return;

        const financials = getOrderFinancials(groupOrders, mode);
        const row: Record<string, string | number> = {
          'Order Number': orderNumber,
          'Order Date': moment(currentOrder.createdAt).format('MM/DD/YYYY'),
        };

        row['Shipping Method'] = currentOrder?.ShippingMethod?.name || 'N/A';

        if (isAdmin) {
          row['Payment Method'] = getPaymentMethod(currentOrder);
        }

        row.Status = getStatusLabel(currentOrder);
        row['Tracking ID'] = getTrackingId(currentOrder);
        row['Total Order Value'] = financials.totalOrderValue;
        row['Shipping Fee'] = financials.shippingFee;

        // Seller-side fees are sell-history only
        if (mode === 'sell') {
          row['Transaction Fee'] = financials.transactionFee;
          row['TradeNetRx Fee'] = financials.tradeNetRxFee;
          row['FedEx Insurance'] = financials.fedExInsurance;
        }

        row[mode === 'buy' && isAdmin ? 'Total Amount' : 'Credit Total'] =
          mode === 'buy'
            ? getBuyerCreditTotal(groupOrders)
            : financials.creditTotal;

        rows.push(row);
      });
  });

  return rows;
};

const downloadWorkbook = (workbook: XLSX.WorkBook, filename: string) => {
  const workbookArray = XLSX.write(workbook, {
    bookType: 'xlsx',
    type: 'array',
  });
  const blob = new Blob([workbookArray], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const countGroupedOrders = (orders: GroupedOrders): number => {
  let count = 0;
  Object.keys(orders).forEach((orderKey) => {
    count += Object.keys(orders[orderKey] || {}).length;
  });
  return count;
};

export const exportOrderHistoryToExcel = ({
  orders,
  fromDate,
  toDate,
  mode,
  isAdmin = false,
}: ExportOrderHistoryParams): void => {
  const rows = buildOrderRows({ orders, mode, isAdmin });
  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Order History');
  downloadWorkbook(workbook, buildFilename(mode, isAdmin, fromDate, toDate, 'xlsx'));
};

const LOGO_ASPECT = 188 / 54;

const loadLogoDataUrl = (): Promise<string | null> =>
  new Promise((resolve) => {
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = 376;
        canvas.height = 108;
        const context = canvas.getContext('2d');
        if (!context) {
          resolve(null);
          return;
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    image.onerror = () => resolve(null);
    image.src = logoSrc;
  });

export const exportOrderHistoryToPdf = async ({
  orders,
  fromDate,
  toDate,
  mode,
  isAdmin = false,
}: ExportOrderHistoryParams): Promise<void> => {
  const rows = buildOrderRows({ orders, mode, isAdmin });
  const headers = rows[0] ? Object.keys(rows[0]) : [];
  const body = rows.map((row) => headers.map((header) => String(row[header] ?? '')));
  const from = fromDate ? fromDate.format('MM/DD/YYYY') : 'start';
  const to = toDate ? toDate.format('MM/DD/YYYY') : 'end';

  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
  const logo = await loadLogoDataUrl();
  const drawLogo = () => {
    if (!logo) return;
    const pageWidth = doc.internal.pageSize.getWidth();
    const logoHeight = 32;
    const logoWidth = logoHeight * LOGO_ASPECT;
    doc.addImage(logo, 'PNG', pageWidth - 24 - logoWidth, 14, logoWidth, logoHeight);
  };

  doc.setFontSize(14);
  doc.text(getExportTitle(mode, isAdmin), 40, 36);
  doc.setFontSize(10);
  doc.text(`${from} – ${to}`, 40, 54);
  drawLogo();

  autoTable(doc, {
    head: [headers],
    body,
    startY: 68,
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [25, 118, 210] },
    margin: { left: 24, right: 24, top: 56 },
    didDrawPage: (data) => {
      if (data.pageNumber > 1) drawLogo();
    },
  });

  doc.save(buildFilename(mode, isAdmin, fromDate, toDate, 'pdf'));
};
